<script lang="ts">
    import { onMount } from 'svelte';
    import { api } from '../../framework/js/api.js';
    import { push } from 'svelte-spa-router';

    interface SystemStats {
        totalUsers: number;
        totalBriefs: number;
        completedBriefs: number;
        pendingProposals: number;
        totalInvoices: number;
        [key: string]: number;
    }

    let isLoading = true;
    let stats: SystemStats = {
        totalUsers: 0,
        totalBriefs: 0,
        completedBriefs: 0,
        pendingProposals: 0,
        totalInvoices: 0
    };

    onMount(async () => {
        await loadStats();
    });

    async function loadStats() {
        isLoading = true;
        try {
            // Fetch admin stats from multiple endpoints
            const [usersResponse, briefsResponse, proposalsResponse] = await Promise.all([
                api.crudIndex('user', {}) as Promise<any>,
                api.makeRequest('GET', '/api/v1/xplorit-brief') as Promise<any>,
                (api.getProposalRequests as any)('pending', null)
            ]);

            if (usersResponse.success && usersResponse.data) {
                stats.totalUsers = Array.isArray(usersResponse.data) ? usersResponse.data.length : 0;
            }

            if (briefsResponse.success && briefsResponse.data) {
                const briefs = Array.isArray(briefsResponse.data) ? briefsResponse.data : [];
                stats.totalBriefs = briefs.length;
                stats.completedBriefs = briefs.filter((b: any) => b.xplorit_status?.name === 'Completed').length;
            }

            if (proposalsResponse.success && proposalsResponse.data) {
                stats.pendingProposals = Array.isArray(proposalsResponse.data) ? proposalsResponse.data.length : 0;
            }

        } catch (error) {
            console.error('Failed to load admin stats:', error);
        } finally {
            isLoading = false;
        }
    }

    function navigateTo(path: string) {
        push(path);
    }

    const statCards = [
        {
            label: 'Total Users',
            key: 'totalUsers' as keyof SystemStats,
            icon: 'users',
            color: '#056cf2',
            href: '/admin/users'
        },
        {
            label: 'Total Briefs',
            key: 'totalBriefs' as keyof SystemStats,
            icon: 'file text',
            color: '#45c496',
            href: '/admin/briefs'
        },
        {
            label: 'Completed Briefs',
            key: 'completedBriefs' as keyof SystemStats,
            icon: 'check circle',
            color: '#8b5cf6',
            href: '/admin/briefs'
        },
        {
            label: 'Pending Proposals',
            key: 'pendingProposals' as keyof SystemStats,
            icon: 'envelope open outline',
            color: '#f59e0b',
            href: '/admin/proposal-requests'
        }
    ];

    const quickActions = [
        {
            label: 'Activity Log',
            description: 'View system activity (signups, logins, briefs, uploads, plan changes)',
            icon: 'history',
            href: '/admin/activity-log',
            highlight: true
        },
        {
            label: 'Manage Users',
            description: 'View and manage all system users',
            icon: 'users',
            href: '/admin/users'
        },
        {
            label: 'View All Briefs',
            description: 'Browse and review all user briefs',
            icon: 'file text',
            href: '/admin/briefs'
        },
        {
            label: 'BtoB Questions',
            description: 'Manage brief question templates',
            icon: 'comment',
            href: '/admin/brief-questions'
        },
        {
            label: 'Proposal Requests',
            description: 'Review incoming proposal requests',
            icon: 'envelope open outline',
            href: '/admin/proposal-requests'
        },
        {
            label: 'Invoices',
            description: 'View all system invoices',
            icon: 'file alternate outline',
            href: '/admin/invoices'
        }
    ];
</script>

<div class="admin-dashboard-home">
    <div class="page-header">
        <h1 class="page-title">Admin Dashboard</h1>
        <p class="page-subtitle">System overview and management</p>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
        {#each statCards as card}
            <button 
                class="stat-card" 
                on:click={() => navigateTo(card.href)}
                style="--card-color: {card.color}"
            >
                <div class="stat-icon" style="background: {card.color}20; color: {card.color}">
                    <i class="icon {card.icon}"></i>
                </div>
                <div class="stat-content">
                    <span class="stat-value">
                        {#if isLoading}
                            <span class="loading-placeholder"></span>
                        {:else}
                            {stats[card.key]}
                        {/if}
                    </span>
                    <span class="stat-label">{card.label}</span>
                </div>
            </button>
        {/each}
    </div>

    <!-- Quick Actions -->
    <div class="section">
        <h2 class="section-title">Quick Actions</h2>
        <div class="actions-grid">
            {#each quickActions as action}
                <button 
                    class="action-card"
                    class:highlight={action.highlight}
                    on:click={() => navigateTo(action.href)}
                >
                    <div class="action-icon">
                        <i class="icon {action.icon}"></i>
                    </div>
                    <div class="action-content">
                        <h3 class="action-title">{action.label}</h3>
                        <p class="action-description">{action.description}</p>
                    </div>
                    <i class="icon chevron right action-arrow"></i>
                </button>
            {/each}
        </div>
    </div>
</div>

<style>
    .admin-dashboard-home {
        margin: 0 auto;
        padding: 2rem;
        min-height: 100%;
    }

    .page-header {
        margin-bottom: 1.5rem;
    }

    .page-title {
        font-size: 2rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.5rem 0;
    }

    .page-subtitle {
        color: var(--grey-secondary);
        font-size: 1rem;
        margin: 0;
    }

    /* Stats Grid */
    .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 1.25rem;
        margin-bottom: 2rem;
    }

    .stat-card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.25rem;
        background: var(--white);
        border-radius: 10px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 1px solid var(--border-color);
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: left;
        width: 100%;
    }

    .stat-card:hover {
        border-color: var(--card-color);
        transform: translateY(-2px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }

    .stat-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 12px;
        flex-shrink: 0;
    }

    .stat-icon i {
        font-size: 1.5rem;
        margin: 0;
    }

    .stat-content {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }

    .stat-value {
        font-size: 1.75rem;
        font-weight: 500;
        color: var(--grey);
        line-height: 1;
    }

    .stat-label {
        font-size: 0.875rem;
        color: var(--grey-secondary);
    }

    .loading-placeholder {
        display: inline-block;
        width: 40px;
        height: 28px;
        background: linear-gradient(90deg, var(--greyish) 25%, #e8e8e8 50%, var(--greyish) 75%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        border-radius: 4px;
    }

    @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }

    /* Section */
    .section {
        margin-top: 2rem;
    }

    .section-title {
        font-size: 1.375rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 1.25rem 0;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid var(--greyish);
    }

    /* Actions Grid */
    .actions-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.25rem;
    }

    .action-card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.25rem 1.5rem;
        background: var(--white);
        border-radius: 12px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        border: 1px solid var(--border-color);
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: left;
        width: 100%;
    }

    .action-card:hover {
        background: var(--greyish);
        border-color: var(--primary);
    }

    .action-card.highlight {
        border-left: 4px solid var(--primary);
        background: linear-gradient(to right, rgba(5, 108, 242, 0.04), var(--white));
    }

    .action-card.highlight .action-icon {
        background: var(--primary);
        color: var(--white);
    }

    .action-card:hover .action-arrow {
        transform: translateX(4px);
        color: var(--primary);
    }

    .action-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 10px;
        background: var(--primary);
        color: var(--white);
        flex-shrink: 0;
    }

    .action-icon i {
        font-size: 1.25rem;
        margin: 0;
    }

    .action-content {
        flex: 1;
        min-width: 0;
    }

    .action-title {
        font-size: 1rem;
        font-weight: 500;
        color: var(--grey);
        margin: 0 0 0.25rem 0;
    }

    .action-description {
        font-size: 0.875rem;
        color: var(--grey-secondary);
        margin: 0;
    }

    .action-arrow {
        color: var(--grey-secondary);
        font-size: 1rem;
        margin: 0 !important;
        transition: transform 0.2s ease, color 0.2s ease;
    }

    @media (max-width: 1024px) {
        .stats-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .actions-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 768px) {
        .admin-dashboard-home {
            padding: 1rem;
        }

        .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
        }

        .stat-card {
            padding: 1rem;
            flex-direction: column;
            text-align: center;
        }

        .stat-icon {
            width: 48px;
            height: 48px;
        }

        .stat-value {
            font-size: 1.5rem;
        }

        .action-card {
            padding: 1rem;
        }

        .action-icon {
            width: 40px;
            height: 40px;
        }
    }

    @media (max-width: 480px) {
        .stats-grid {
            grid-template-columns: 1fr;
        }

        .stat-card {
            flex-direction: row;
            text-align: left;
        }
    }
</style>
